import { Injectable } from '@angular/core';
import { environment } from '@config/environments/environment';
import { DATE } from '@shared/helpers/date-time/date';

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

/**
 * LoggerService
 * - Hỗ trợ các cấp độ log: DEBUG, INFO, WARN, ERROR
 * - Cho phép bật/tắt log theo environment hoặc runtime (localStorage)
 * - Có thể mở rộng để gửi log về API server
 */
@Injectable({ providedIn: 'root' })
export class LoggerService {
  // ⚙️ Cho phép bật/tắt log bằng environment hoặc localStorage
  private isEnabled: boolean;
  private logLevel: LogLevel = LogLevel.DEBUG;

  constructor() {
    const debugFlag = localStorage.getItem('debug');
    this.isEnabled = environment.dev.enableDebugLog || debugFlag === 'true';

    // Đọc log level từ localStorage
    const storedLevel = localStorage.getItem('logLevel');
    if (storedLevel) {
      this.logLevel = parseInt(storedLevel, 10);
    }
  }

  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
    localStorage.setItem('logLevel', level.toString());
  }

  /** Bật log runtime (chỉ cần gọi khi cần debug production) */
  enableRuntimeLogging(): void {
    localStorage.setItem('debug', 'true');
    this.isEnabled = true;
    this.info('✅ Logger enabled at runtime');
  }

  /** Tắt log runtime */
  disableRuntimeLogging(): void {
    localStorage.removeItem('debug');
    this.isEnabled = !environment.dev.enableDebugLog;
    this.info('🚫 Logger disabled at runtime');
  }

  // 🪵 Các mức độ log

  debug(...args: any[]): void {
    if (this.isEnabled && this.logLevel <= LogLevel.DEBUG) {
      console.debug('🐞', this.timestamp(), ...args);
    }
  }

  info(...args: any[]): void {
    if (this.isEnabled && this.logLevel <= LogLevel.INFO) {
      console.info('ℹ️', this.timestamp(), ...args);
    }
  }

  warn(...args: any[]): void {
    if (this.isEnabled && this.logLevel <= LogLevel.WARN) {
      console.warn('⚠️', this.timestamp(), ...args);
    }
  }

  error(...args: any[]): void {
    if (this.isEnabled) console.error('❌', this.timestamp(), ...args);
    // 👉 Bạn có thể mở rộng ở đây để gửi lỗi lên server API
    this.sendToServer('error', args);
  }

  // ✅ Helper methods
  private timestamp(): string {
    return '';
    // return DATE.now();
  }

  // ✅ Group logs
  group(label: string, callback: () => void): void {
    if (this.isEnabled) {
      console.group(label);
      callback();
      console.groupEnd();
    }
  }

  // ✅ Table logs
  table(data: any): void {
    if (this.isEnabled) {
      console.table(data);
    }
  }

  // 📡 Mẫu hàm gửi log về API (nếu muốn)
  private sendToServer(level: string, message: any): void {
    // Ví dụ: gọi API log (chỉ chạy khi production)
    if (environment.dev.enableServerLog) {
      fetch(`http://localhost:3000/logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ level, message, time: new Date() }),
      });
    }
  }
}
